import React from 'react';
import { Skeleton } from '../../util/skeleton/Skeleton';
import { formatDate } from '../../util/araknerdlib';
import { Link } from 'react-router-dom';

const SkeletonSessionItem = () => (
  <div className="mb-2">
    <div className="d-flex align-items-center justify-content-between mb-1">
      <Skeleton w="35%" h={14} />
      <Skeleton w="80px" h={10} />
    </div>
    <Skeleton w="60%" h={12} />
    <Skeleton w="45%" h={10} />
  </div>
);

const EmptyState = () => (
  <div className="text-center text-muted py-4">
    <div className="mb-2">
      <i className="fe fe-wifi-off tx-30 d-block mb-2" />
    </div>
    <div className="tx-14">No recent device activity</div>
    <small className="tx-11 d-block mt-1">
      Devices will appear here as they connect to your network.
    </small>
  </div>
);

const RecentWifiSessions = ({ sessions, goToProfile }) => {
  const isLoading = sessions === null || sessions === undefined;
  const hasData = Array.isArray(sessions) && sessions.length > 0;

  const totalDevices = hasData ? sessions.length : 0;
  const onlineDevices = hasData ? sessions.filter((s) => s.isOnline).length : 0;
  const offlineDevices = hasData ? totalDevices - onlineDevices : 0;

  const getStatusBadgeClass = (session) => {
    if (session.isOnline) return 'badge bg-success';
    if ((session.raw_status || '').toLowerCase() === 'disconnected') return 'badge bg-secondary';
    return 'badge bg-primary';
  };

  const getStatusText = (session) => {
    if (session.isOnline) return 'Online';
    return (session.raw_status || session.activity || 'Unknown').toString();
  };

  const normalizeDate = (value) => {
    if (!value) return 'Never';
    return formatDate(value);
  };


  return (
    <div className="card custom-card">
      {/* Header */}
      <div className="card-body pb-1">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <h6 className="card-title mb-1">Recent Device Activity</h6>
            <p className="text-muted mb-0 card-sub-title tx-12">
              Latest device heartbeats and connectivity status
            </p>
          </div>

          {/* Tiny summary chips */}
          {hasData && (
            <div className="text-end">
              <div className="tx-11 mb-1">
                <span className="badge bg-success-light me-1">
                  Online: <strong>{onlineDevices}</strong>
                </span>
                <span className="badge bg-secondary-light">
                  Offline: <strong>{offlineDevices}</strong>
                </span>
              </div>
              <small className="tx-10 text-muted">
                Total devices: {totalDevices}
              </small>
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="card-body pt-0 overflow-auto" style={{ maxHeight: '200px' }}>
        {/* Loading state */}
        {isLoading && (
          <>
            {[...Array(5)].map((_, i) => (
              <SkeletonSessionItem key={i} />
            ))}
          </>
        )}

        {/* Empty state */}
        {!isLoading && (sessions === '404' || !hasData) && <EmptyState />}

        {/* Data list */}
        {!isLoading && hasData && (
          <div className="activity-block">
            <ul className="task-list mt-1">
            {sessions.map((session, index) => {
              const deviceName = session.device_name || {};
              const nickname =
                deviceName.nickname && deviceName.nickname !== 'Unknown Device'
                  ? deviceName.nickname
                  : 'Unnamed Device';

              const manufacturer =
                deviceName.manufacturer && deviceName.manufacturer !== 'Unknown Manufacturer'
                  ? deviceName.manufacturer
                  : null;

              const os =
                deviceName.os && deviceName.os !== 'Unknown OS'
                  ? deviceName.os
                  : null;

              const mac = (session.detail || '').includes('MAC:')
                ? session.detail.split('MAC:')[1].split('- IP:')[0].trim()
                : 'Unknown';

              const ip = (session.detail || '').includes('IP:')
                ? session.detail.split('IP:')[1].trim()
                : 'Unknown';

              const key =
                session.device_token ||
                session.voucher_code ||
                `${mac}-${index}`;

              const statusBadgeClass = getStatusBadgeClass(session);
              const statusText = getStatusText(session);
              const isLast = index === sessions.length - 1;
              return (
                <li key={key} className={isLast ? 'mb-0' : ''}>
                  <i className={`task-icon ${session.statusColor || 'bg-secondary'}`}></i>
                <div
                  className="border-0 border-bottom"
                >
                  <div className="d-flex align-items-start">

                    {/* Content */}
                    <div className="flex-grow-1">
                      {/* Top row: nickname + status + last seen */}
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <div className="d-flex flex-column">
                          <div className="d-flex align-items-center">
                            <span className="tx-13 fw-semibold me-2">
                              {nickname}
                            </span>
                            <span className={statusBadgeClass}>
                              {statusText}
                            </span>
                          </div>
                          <small className="text-muted tx-11">
                            {manufacturer && <span>{manufacturer}</span>}
                            {manufacturer && os && <span> · </span>}
                            {os && <span>{os}</span>}
                            {!manufacturer && !os && (
                              <span className="text-muted">Unknown device type</span>
                            )}
                          </small>
                        </div>

                        <div className="text-end">
                          <small className="d-block tx-11 text-muted">
                            Last heartbeat:
                          </small>
                          <small className="tx-11">
                            {normalizeDate(session.updated_at)}
                          </small>
                        </div>
                      </div>

                      {/* Middle row: MAC / IP */}
                      <div className="d-flex flex-wrap align-items-center tx-12 mb-1">
                        <span className="me-3">
                          <i className="fe fe-hash me-1 tx-12" />
                          <strong>MAC:</strong> <span className='link text-primary' onClick={()=>goToProfile(session.device_token)}>{mac || 'Unknown'}</span>
                        </span>
                        <span className="me-3">
                          <i className="fe fe-map-pin me-1 tx-12" />
                          <strong>IP:</strong> {ip || 'Unknown'}
                        </span>
                        {session.created_at && (
                          <span className="me-3 text-muted ml-2">
                            <i className="fe fe-clock me-1 tx-12" />
                            Since: {normalizeDate(session.created_at)}
                          </span>
                        )}
                      </div>

                      {/* Bottom row: voucher / token + optional description */}
                      <div className="d-flex flex-wrap align-items-center justify-content-between tx-11">
                        <div className="d-flex flex-wrap align-items-center">
                          {session.voucher_code && (
                            <span className="badge bg-primary-light me-2 mb-1">
                              Voucher: <strong>{session.voucher_code}</strong>
                            </span>
                          )}
                          {session.device_token && (
                            <span className="badge bg-info-light mb-1">
                              Token:{' '}
                              <strong>
                                {session.device_token.length > 16
                                  ? session.device_token.slice(0, 16) + '…'
                                  : session.device_token}
                              </strong>
                            </span>
                          )}
                          {!session.voucher_code && !session.device_token && (
                            <span className="text-muted mb-1">
                              No voucher/device token assigned
                            </span>
                          )}
                        </div>

                        {session.status_description && (
                          <small className="text-muted text-end ms-2 mb-1">
                            {session.status_description}
                          </small>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </li>
              );
            })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecentWifiSessions;
